import { useQuery } from '@tanstack/react-query';

import useNavigation from '@Navigation/useNavigation';
import { useRequest } from '@Contexts/Request';
import * as ApiMeta from '@Api/Meta';
import cutOutRegionSlug from '@Utils/cutOutRegionSlug';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import usePromoRegions from '@Queries/Promo/usePromoRegions';
import getCookie from '@Utils/getCookie';
import { useDevPanel } from '@Stores/DevPanel';

/**
 * NOTE: Данный запрос определяет - требуется ли  на текущей странице отобразить анонсную строку. Т.к. для проверки некоторых условий, необходимо передавать cookie,
 * запрос выполняется на стороне клиента, после загрузки основного контента страницы.
 */
function useInfoStripe() {
  const featureFlags = useFeatureFlags();
  const { pathname: path } = useNavigation();
  const request = useRequest();
  const { currentDate: devCurrentDate } = useDevPanel();

  const {
    region,
    language: { id: lang },
    device,
  } = request;

  const { data: regions } = usePromoRegions();

  const pathname = cutOutRegionSlug(path, request);

  const keys = ['info-stripe', region, lang];

  const query = useQuery({
    queryKey: keys,
    queryFn: async () => {
      if (featureFlags.infoStripesDisabled) return Promise.resolve(null);

      return ApiMeta.getInfoStripe(request);
    },
    select: (data) => {
      const currentDate = devCurrentDate || new Date();
      let currentRegion = region;
      if (currentRegion === 'moscow' || currentRegion === 'minsk' || currentRegion === 'almaty')
        currentRegion = '';
      const isAllowedRegion = (rgn, type, allRegions) => {
        let allowedRegions = [];
        switch (type) {
          case 'all':
            allowedRegions = allRegions;
            break;
          case 'only-franchisee':
            allowedRegions = allRegions.filter((r) => r.isFranchisee);
            break;
          case 'not-franchisee':
            allowedRegions = allRegions.filter((r) => !r.isFranchisee);
            break;
          case 'none':
            allowedRegions = [];
            break;
          default:
            allowedRegions = allRegions;
        }
        const isAllowed = allowedRegions.some((r) => r.slug.trim() === rgn.trim());

        return isAllowed;
      };

      if (!Array.isArray(data) || device.bot) return null;

      // Фильтрация необходимых условий за 1 проход
      const dataFiltered = data.filter(
        (infoStripe) =>
          (infoStripe.includedRegions?.length
            ? infoStripe.includedRegions.some((r) => r.name.trim() === currentRegion)
            : true) &&
          (infoStripe.excludedRegions?.length
            ? !infoStripe.excludedRegions.some((r) => r.name.trim() === currentRegion)
            : true) &&
          (infoStripe.includedLinks?.length
            ? infoStripe.includedLinks.some((link) =>
                pathname.endsWith('*')
                  ? link.trim().startsWith(pathname.slice(0, -1))
                  : link.trim() === pathname,
              )
            : true) &&
          (infoStripe.excludedLinks?.length
            ? !infoStripe.excludedLinks.some((link) =>
                pathname.endsWith('*')
                  ? link.trim().startsWith(pathname.slice(0, -1))
                  : link.trim() === pathname,
              )
            : true) &&
          new Date(infoStripe.startDate) <= currentDate &&
          new Date(infoStripe.endDate) >= currentDate &&
          getCookie(infoStripe.cookie) !== '1' &&
          isAllowedRegion(currentRegion, infoStripe.allowedRegions?.name, regions),
      );

      if (!dataFiltered.length) return null;

      const infoStripe = dataFiltered[0];

      if (Array.isArray(infoStripe.timer)) {
        infoStripe.timer = infoStripe.timer[0] || null;
      }

      return infoStripe;
    },
    enabled: true,
    placeholderData: (previousData) => previousData,
  });

  return query;
}

export default useInfoStripe;
